import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_close_default, QIcon_refresh, QIcon_search_default } from '@repo/icons/index'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useEventTracking } from '../../../../../helpers/gtmHelper'
import { ITutor, TutorKeyType } from '../../../../../model/tutor.interface'
import { useSearchTutors } from '../../../../../modules/apis/student/tutors/queries'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import { useOnclickHeartTutor } from '../../../../../modules/hooks/common/useOnclickHeartTutor'
import { useTutorProfileSideModal } from '../../../../../modules/hooks/common/useTutorProfileSideModal'
import useFilterItems from '../../../../../modules/hooks/useFilterItems'
import useReturnFullUrl from '../../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_book_lessons, n_tutor, n_tutor_lists } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { deepEqual, isB2BHRGroup, isMulticampus } from '../../../../../modules/utils'
import { COLORS } from '../../../../../modules/vars'
import { useTutorStack } from '../../../../../pages/[lang]/webview/tutors'
import { useSidePopup } from '../../../../basic/popup/SidePopup'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import Pagination from '../../../../common/Pagination'
import TutorCard from '../../../../common/TutorCard'
import CompletedLesson from '../../../../new-portal/lessons/schedules/CompletedLesson'
import Filter from '../Filter'
import FilterMobile from '../FilterMobile'
import { TUTOR_PROFILE_TAB } from '../TutorBody'

interface FilteredListProps {
  payload: ITutorSearchPayload
  setPayload: (payload: ITutorSearchPayload) => void
}

const FilteredList = ({ payload, setPayload }: FilteredListProps) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const isB2BUser = isB2BHRGroup(currentUser)
  const { strengthsItems, fieldOfStudiesItems, experienceItems, accentItems, genderItems } = useFilterItems()

  return (
    <>
      {payload?.is_featured && (
        <span className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap">
          {t(n_tutor.category.featured)}
          <div
            className="cursor-pointer"
            onClick={() => {
              setPayload({
                ...payload,
                is_featured: false,
              })
            }}
          >
            <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
          </div>
        </span>
      )}
      {payload?.is_pointback && !isB2BUser && (
        <span className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap">
          {t(n_tutor.category.point_back)}
          <div
            className="cursor-pointer"
            onClick={() => {
              setPayload({
                ...payload,
                is_pointback: false,
              })
            }}
          >
            <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
          </div>
        </span>
      )}
      {payload?.field_of_study.length > 0 &&
        payload?.field_of_study.map((item) => (
          <span
            key={item}
            className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap"
          >
            {fieldOfStudiesItems.find((fieldOfStudy) => fieldOfStudy.value === item)?.text}
            <div
              className="cursor-pointer"
              onClick={() => {
                setPayload({
                  ...payload,
                  field_of_study: payload.field_of_study.filter((fieldOfStudy) => fieldOfStudy !== item),
                })
              }}
            >
              <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
            </div>
          </span>
        ))}
      {payload?.strength.length > 0 &&
        payload?.strength.map((item) => (
          <span
            key={item}
            className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap"
          >
            {strengthsItems.find((strength) => strength.value === item)?.text}
            <div
              className="cursor-pointer"
              onClick={() => {
                setPayload({
                  ...payload,
                  strength: payload.strength.filter((strength) => strength !== item),
                })
              }}
            >
              <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
            </div>
          </span>
        ))}
      {payload?.experience.length > 0 &&
        payload?.experience.map((item) => (
          <span
            key={item}
            className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap"
          >
            {experienceItems.find((experience) => experience.value === item)?.text}
            <div
              className="cursor-pointer"
              onClick={() => {
                setPayload({
                  ...payload,
                  experience: payload.experience.filter((experience) => experience !== item),
                })
              }}
            >
              <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
            </div>
          </span>
        ))}
      {payload?.gender.length > 0 &&
        payload?.gender.map((item) => (
          <span
            key={item}
            className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap"
          >
            {genderItems.find((gender) => gender.value === item)?.text}
            <div
              className="cursor-pointer"
              onClick={() => {
                setPayload({
                  ...payload,
                  gender: payload.gender.filter((gender) => gender !== item),
                })
              }}
            >
              <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
            </div>
          </span>
        ))}
      {payload?.accent.length > 0 &&
        payload?.accent.map((item) => (
          <span
            key={item}
            className="Caption-12Md sm:Body2-14Bd text-white bg-gray-700 py-[8px] px-[10px] rounded-[8px] flex items-center gap-x-[4px] whitespace-nowrap"
          >
            {accentItems.find((accent) => accent.value === item)?.text}
            <div
              className="cursor-pointer"
              onClick={() => {
                setPayload({
                  ...payload,
                  accent: payload.accent.filter((accent) => accent !== item),
                })
              }}
            >
              <QIcon_close_default width={14} height={14} color={COLORS.WHITE} />
            </div>
          </span>
        ))}
    </>
  )
}

interface ITutorListPropsType {
  type: TutorKeyType
}

interface ITutorSearchPayload {
  search: string
  is_featured: boolean
  is_pointback: boolean
  gender: number[]
  accent: number[]
  field_of_study: number[]
  strength: number[]
  experience: number[]
}

const initialPayload = {
  search: '',
  is_featured: false,
  is_pointback: false,
  gender: [],
  accent: [],
  field_of_study: [],
  strength: [],
  experience: [],
}

const TutorListBody = ({ type }: ITutorListPropsType) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const { useTutorFlow } = useTutorStack()
  const { push } = useTutorFlow()
  const router = useAppRouter({ stackPush: push })
  const { currentUser } = useCurrentUser()
  const [isUserMulcam, setIsUserMulcam] = useState<boolean>(true)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [selectedTutor, setSelectedTutor] = useState()
  const [result, setResult] = useState()
  const [payload, setPayload] = useState<ITutorSearchPayload>(initialPayload)
  const { sendPageViewEvent, sendClickEvent } = useEventTracking()
  const showTutorProfileSideModal = useTutorProfileSideModal({
    overlayClose: true,
    bgWhite: false,
  })
  const sidePopup = useSidePopup()
  const onCloseSidePopup = () => {
    sidePopup.close('tutor-popup')
  }

  useEffect(() => {
    if (currentUser) {
      setIsUserMulcam(isMulticampus(currentUser))
    }
  }, [currentUser])

  useEffect(() => {
    if (result) {
      onCloseSidePopup()
    }
  }, [result])
  const [listType, setListType] = useState<TutorKeyType>(type) //type은 초기값, listType값을 기준으로 All / Recommend 구분

  const [tutors, setTutors] = useState<ITutor[]>([])
  const postsPerPage = 12
  const totalPosts = count
  const [activeIndex, setActiveIndex] = useState(0)
  const { mutate } = useSearchTutors()

  useEffect(() => {
    onChangeFilter(true)
  }, [activeIndex])

  const onClickHeartTutor = useOnclickHeartTutor({
    isList: true,
    tutorItems: tutors,
    setTutorItems: setTutors,
  })

  const onClickHeartTutorTab = async (e: React.MouseEvent, tutor, type, showToast = true) => {
    onClickHeartTutor(e, tutor, type, showToast) //toast
    onChangeFilter(true)
  }

  const handlePagination = async (number) => {
    window.scrollTo(0, 0)

    sendClickEvent({
      button_name: 'pagination',
      object_title: number,
    })
    setActiveIndex(number - 1)
  }

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setKeyword(e.target.value)
  }
  const handleClose = () => {
    setKeyword('')
    setPayload({
      ...payload,
      search: '',
    })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setPayload({
        ...payload,
        search: keyword,
      })
    }
  }
  const getFullUrl = useReturnFullUrl()
  const focusRef = useRef(null)
  const handleSchedule = (e, tutor, tab, index) => {
    if (tab == TUTOR_PROFILE_TAB.SUMMARY) {
      sendClickEvent({
        button_name: 'tutor_card',
        location_detail: index + 1,
      })
    } else {
      sendClickEvent({
        button_name: 'tutor_card_book',
        location_detail: index + 1,
      })
    }

    if (isWebview) {
      router.push(urls.student.newPortal.tutors.detail.as(tutor.id, tab), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.tutors.detail.as(tutor.id, tab)),
        },
      })
    } else {
      setSelectedTutor(tutor.id)
      showTutorProfileSideModal(
        e,
        tutor.id,
        tutor.is_featured_tutor,
        () => {
          setSelectedTutor(null)
        },
        tab,
        setResult
      )
    }
  }
  const onChangeFilter = async (isPagination = false) => {
    setLoading(true)
    if (!isPagination) {
      setActiveIndex(0)
    }
    const tempPayload = {
      ...payload,
      type: 'all',
      experience_work: payload.experience.includes(1),
      experience_graduate: payload.experience.includes(2),
      page: isPagination ? activeIndex + 1 : 1,
      size: postsPerPage,
    }
    mutate(tempPayload, {
      onSuccess: (response) => {
        setTutors(response?.result)
        setCount(response.count)
        setLoading(false)
        if (response.count === 0) {
          if (tempPayload.search) {
            sendPageViewEvent({
              page_name: 'tutor_null',
              object_title: 'search',
            })
          } else {
            sendPageViewEvent({
              page_name: 'tutor_null',
              object_title: 'filter',
            })
          }
        }
      },
    })
  }

  return result ? (
    <div className="w-full px-[16px] sm:px-[32px] lg:mt-[20px] lg:max-w-[688px] mx-auto">
      <CompletedLesson result={result} />
    </div>
  ) : (
    <section className="max-w-[952px] mt-[16px] lg:mt-[40px]">
      <div className="flex items-center justify-between px-[16px] lg:px-[0px]">
        <div className="Hero4-32Bd hidden lg:block">{t(n_tutor_lists.title)}</div>
        <div className="flex h-[48px] items-center rounded-[6px] border-[1px] border-gray-200 bg-gray-50 p-[12px] sm:mt-[32px] sm:min-w-[336px] sm:max-w-[336px] w-full">
          <QIcon_search_default width={24} height={24} color={COLORS.GRAY500} className="mr-[8px]" />
          <input
            className={classNames(
              'Body2-14Md w-full flex-grow cursor-pointer resize-none rounded-[8px] border-none bg-gray-50 p-0 text-black !ring-0 focus:border-none focus:outline-none',
              !isMobile && 'Body1-16Md',
              isMobile && 'Body2-14Md'
            )}
            ref={focusRef}
            onFocus={() => {
              sendClickEvent({
                button_name: 'tutor_search',
              })
            }}
            value={keyword}
            onChange={onChange}
            placeholder={t(n_tutor_lists.search_placehoder)}
            draggable={false}
            onKeyDown={handleKeyDown}
          />
          {keyword && (
            <div className="cursor-pointer self-center rounded-full bg-gray-200 p-[2px]" onClick={handleClose}>
              <QIcon_close_default color="#FFFFFF" width={16} height={16} />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-x-[16px] sm:mt-[40px] mt-[16px]">
        <div className="sm:min-w-[216px] hidden sm:block">
          <Filter onChangeFilter={onChangeFilter} payload={payload} setPayload={setPayload} />
        </div>
        <div className="block sm:hidden">
          <FilterMobile onChangeFilter={onChangeFilter} payload={payload} setPayload={setPayload} />
        </div>
        <div className="w-full">
          {!deepEqual(initialPayload, payload) && (
            <div className="flex justify-between mb-[16px] relative">
              <div className="py-[8px] px-[16px] sm:px-[0px] flex flex-row gap-[4px] sm:flex-wrap overflow-x-scroll border-gray-200 border-t-[1px] bg-gray-50 w-full sm:border-none sm:overflow-hidden sm:bg-white">
                <FilteredList payload={payload} setPayload={setPayload} />
                <div className="min-w-[95px] sm:hidden"></div>
              </div>
              <div
                className="px-[16px] justify-center Body2-14Bd text-gray-500 gap-x-[4px] flex items-center cursor-pointer whitespace-nowrap absolute sm:relative right-[0px]
                  custom-gradient h-full"
                onClick={() => {
                  setPayload(initialPayload)
                }}
              >
                <QIcon_refresh />
                {t(n_tutor.btn.reset)}
              </div>
            </div>
          )}
          <div className="px-[16px] sm:px-[0px]">
            <div className="flex items-center justify-between">
              <div className="lg:Body1-16Md text-gray-600 Body2-14Bd">{t(n_tutor_lists.numbers(count))}</div>
            </div>
            {loading ? (
              <div className="mt-[16px]">
                <LoadingSpinner show lg primary />
              </div>
            ) : (
              <div className="mt-[16px]">
                <>
                  {tutors.length == 0 && (
                    <div className="flex items-center justify-center mt-[40px]">
                      <div className="py-[40px] flex flex-col justify-center items-center gap-y-[16px]">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.5708 8C14.0364 8 11.9818 10.0897 11.9818 12.6675V51.3325C11.9818 53.9103 14.0364 56 16.5708 56H37.0072C39.1524 56 41.2078 55.1384 42.7118 53.6087L49.4578 46.7473C50.9289 45.2511 51.7532 43.2369 51.7532 41.1386V12.6675C51.7532 10.0897 49.6986 8 47.1642 8H16.5708ZM20.6018 17.5999C19.628 17.5999 18.8386 18.3894 18.8386 19.3632C18.8386 20.337 19.628 21.1265 20.6018 21.1265H30.6776C31.6514 21.1265 32.4409 20.337 32.4409 19.3632C32.4409 18.3894 31.6514 17.5999 30.6776 17.5999H20.6018ZM18.8386 26.9195C18.8386 25.9457 19.6112 25.1562 20.5643 25.1562H40.2872C41.2403 25.1562 42.0129 25.9457 42.0129 26.9195C42.0129 27.8933 41.2403 28.6828 40.2872 28.6828H20.5643C19.6112 28.6828 18.8386 27.8933 18.8386 26.9195ZM20.6018 32.7152C19.628 32.7152 18.8386 33.5046 18.8386 34.4784C18.8386 35.4523 19.628 36.2417 20.6018 36.2417H35.7155C36.6894 36.2417 37.4788 35.4523 37.4788 34.4784C37.4788 33.5046 36.6894 32.7152 35.7155 32.7152H20.6018Z"
                            fill="#E4E7F4"
                          />
                        </svg>
                        <div>
                          <div className="Title2-18Bd text-center">{t(n_book_lessons.filter.empty_desc_title)}</div>
                          <div className="Body1-16Md mt-[12px] text-center text-gray-500">
                            {t(n_book_lessons.filter.empty_desc_subitle)}
                          </div>
                        </div>
                        <div>
                          <DSButton
                            size={BUTTON_SIZE.MD}
                            type={BUTTON_TYPE.OUTLINE}
                            onClick={() => {
                              setPayload(initialPayload)
                            }}
                          >
                            <DSButton.Label>{t(n_tutor.btn.reset_filter)}</DSButton.Label>
                          </DSButton>
                        </div>
                      </div>
                    </div>
                  )}
                  {tutors.length > 0 && (
                    <div className="mb-[40px] grid grid-cols-1 gap-[16px]">
                      {tutors.map((tutor: ITutor, index) => (
                        <TutorCard
                          isRecommend={tutor.is_rising_star}
                          key={tutor.id}
                          currentUser={currentUser}
                          selected={selectedTutor == tutor.id}
                          handleAvatarClick={(e, tutor) => handleSchedule(e, tutor, TUTOR_PROFILE_TAB.SUMMARY, index)}
                          handleAvailableButtonClick={(e, tutor) =>
                            handleSchedule(e, tutor, TUTOR_PROFILE_TAB.SCHEDULE, index)
                          }
                          onClickHeart={(e) => onClickHeartTutorTab(e, tutor, listType)}
                          tutor={tutor}
                        />
                      ))}
                    </div>
                  )}
                </>

                <div className="flex flex-row items-center justify-center gap-[8px] px-[20px] pb-[20px]">
                  <Pagination
                    postsPerPage={postsPerPage}
                    totalPosts={totalPosts}
                    activeIndex={activeIndex}
                    paginate={handlePagination}
                    setActiveIndex={setActiveIndex}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default TutorListBody
